import type { EmailAttachmentControlProps } from '@/components/affiliate/bulk-email/EmailAttachmentsField';
import { Button, Flex, Heading, HStack, IconButton, VStack } from '@chakra-ui/react';
import type { EditorState } from 'lexical';
import { useCallback, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type AvailableVariable } from '@fluenceur/ui';
import RichTextMessageEditor, { type RichTextMessageEditorChange } from './RichTextMessageEditor';
import { Eye, EyeClosed, Undo } from 'iconoir-react';

interface EmailInvitationMessageEditorProps {
  initialContent: string | null;
  availableVariables: AvailableVariable[];
  /**
   * `html` is populated only when `includeHtml` is set. It serializes variable
   * chips back to `{{key}}` mustache text, ready for server-side rendering.
   */
  onContentChange: (editorState: EditorState, plainText: string, html?: string) => void;
  /**
   * Compile the body to HTML on every change (variable chips become `{{key}}`).
   * Off by default: hosts that only need the Lexical state pay nothing.
   */
  includeHtml?: boolean;
  /** When provided, a Reset button is shown that clears the editor. */
  onReset?: () => void;
  /** Header heading. Defaults to the invitation editor title. */
  title?: string;
  /** Hide the header row (heading + reset). Hosts that label the field can omit it. */
  showHeader?: boolean;
  /** Editor placeholder. Defaults to the invitation email placeholder. */
  placeholder?: string;
  /** "Insert variable" button label. */
  insertVariableLabel?: string;
  /** Accessible label for the editable area. Defaults to the title. */
  ariaLabel?: string;
  /**
   * When provided, a preview toggle (eye) is added to the toolbar. Toggling on
   * swaps the editable area for this node while keeping the editor mounted
   * (so Lexical history is preserved). Reuses the toolbar's trailing slot, the
   * same extension point the bulk-email composer uses for its preview.
   */
  previewContent?: ReactNode;
  /** Accessible label for the preview toggle button. */
  previewLabel?: string;
  /**
   * Node rendered on the right of the bottom bar, across from the "insert
   * variable" button (e.g. a template picker). The row becomes space-between.
   */
  footerTrailing?: ReactNode;
  /** Maximum height for the scrollable writing area. */
  editorMaxH?: string;
  attachmentControl?: EmailAttachmentControlProps;
}

export function EmailInvitationMessageEditor({
  initialContent,
  availableVariables,
  onContentChange,
  includeHtml = false,
  onReset,
  title,
  showHeader = true,
  placeholder,
  insertVariableLabel,
  ariaLabel,
  previewContent,
  previewLabel,
  footerTrailing,
  editorMaxH,
  attachmentControl,
}: EmailInvitationMessageEditorProps): ReactElement {
  const { t } = useTranslation();
  const [editorKey, setEditorKey] = useState(0);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const resolvedTitle = title ?? t('invitations.editor.title');
  const resolvedPlaceholder = placeholder ?? t('invitations.editor.emailPlaceholder');
  const resolvedInsertVariableLabel = insertVariableLabel ?? t('invitations.editor.insertVariable');
  const resolvedAriaLabel = ariaLabel ?? resolvedTitle;

  const handleEditorChange = useCallback(
    ({ editorState, plainText, html }: RichTextMessageEditorChange) => {
      onContentChange(editorState, plainText, html);
    },
    [onContentChange]
  );

  const handleReset = useCallback(() => {
    setEditorKey((prev) => prev + 1);
    onReset?.();
  }, [onReset]);

  return (
    <VStack align="stretch" gap={3} w="full" flex={1} minH={0}>
      {showHeader && (
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <Heading as="h3" fontSize="md">
            {resolvedTitle}
          </Heading>
          {onReset && (
            <Button
              variant="ghost"
              size="xs"
              fontSize="sm"
              fontWeight="medium"
              onClick={handleReset}
            >
              <HStack gap={1}>
                <Undo width={16} height={16} />
                <span>{t('invitations.editor.reset')}</span>
              </HStack>
            </Button>
          )}
        </Flex>
      )}

      <RichTextMessageEditor
        editorKey={editorKey}
        namespace="EmailInvitationMessageEditor"
        initialContent={initialContent}
        availableVariables={availableVariables}
        onContentChange={handleEditorChange}
        includeHtml={includeHtml}
        placeholder={resolvedPlaceholder}
        ariaLabel={resolvedAriaLabel}
        insertVariableLabel={resolvedInsertVariableLabel}
        translateFn={t}
        variant="document"
        isPreviewing={isPreviewing}
        previewContent={previewContent}
        toolbarTrailingItems={
          previewContent ? (
            <IconButton
              aria-label={previewLabel ?? t('invitations.editor.preview')}
              size="xs"
              w={6}
              h={6}
              minW={6}
              p={0}
              variant={isPreviewing ? 'solid' : 'ghost'}
              colorPalette="gray"
              rounded="sm"
              onClick={() => setIsPreviewing((value) => !value)}
            >
              {isPreviewing ? (
                <EyeClosed width={15} height={15} strokeWidth={2} />
              ) : (
                <Eye width={15} height={15} strokeWidth={2} />
              )}
            </IconButton>
          ) : undefined
        }
        attachmentControl={attachmentControl}
        footerTrailing={footerTrailing}
        editorMaxH={editorMaxH}
        containerProps={{
          rounded: 'lg',
          border: '1px solid',
          borderColor: 'border',
          bg: 'bg',
          minH: '240px',
          px: 4,
        }}
        footerPaddingY={2}
        editableMinH="140px"
      />
    </VStack>
  );
}
